import React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { METER_PRICES } from '@gitbook/app-pricing';
import { CONTENT_DEFAULT_BRANCH } from '@gitbook/hive-core';
import { useHiveBranchById, useHiveWorkingBranch } from '@gitbook/hive-react';
import { Alert, LoadingIndicator } from '@gitbook/spine-react';

import { PrettyDollarNumber } from '../Billing';

/**
 * Estimate of how many times per month, the content is updated.
 */
const CONTENT_UPDATE_PER_MONTH = 25;

/**
 * Estimate of how many pages are updated per update.
 */
const CONTENT_PAGES_CHANGED_PER_UPDATE = 3;

/**
 * Estimate of how many words are in a page.
 */
const CONTENT_WORDS_PER_PAGE = 300;

type TranslationPriceEstimateProps = {
    organizationId: string;
    spaceId: string;
};

/**
 * Alert to show the price estimate of a translation.
 */
export function TranslationPriceEstimate(props: TranslationPriceEstimateProps) {
    return (
        <React.Suspense fallback={<PriceEstimateLoading />}>
            <PriceEstimateForContent {...props} />
        </React.Suspense>
    );
}

function PriceEstimateLoading() {
    return (
        <Alert
            size="xsmall"
            kind="info"
            icon={LoadingIndicator}
            details={<p>Estimating monthly additional cost</p>}
        />
    );
}

function PriceEstimateForContent(props: TranslationPriceEstimateProps) {
    const { organizationId, spaceId } = props;
    const branch = useHiveBranchById({
        contentId: spaceId,
        branchId: CONTENT_DEFAULT_BRANCH,
    });
    const state = useHiveWorkingBranch(branch);

    const totalPages = React.useMemo(() => {
        return Object.values(state.sourceRevision.pages).length;
    }, [state.sourceRevision]);

    const initialWordsCount = totalPages * CONTENT_WORDS_PER_PAGE;

    const pagesChangedPerMonth =
        Math.min(CONTENT_PAGES_CHANGED_PER_UPDATE, totalPages) * CONTENT_UPDATE_PER_MONTH;
    const wordsChangedPerMonth = pagesChangedPerMonth * CONTENT_WORDS_PER_PAGE;

    const totalWords = initialWordsCount + wordsChangedPerMonth;
    const totalPrice =
        totalWords > METER_PRICES[api.BillingMeter.TranslationWords].startingAt
            ? estimatePriceOfTranslationRun(
                  totalWords - METER_PRICES[api.BillingMeter.TranslationWords].startingAt
              )
            : 0;

    // Track an event with the estimated price
    React.useEffect(() => {
        analytics.logEvent(
            analytics.createOrgEvent(organizationId, {
                type: 'translation_estimate_price',
                space: spaceId,
                totalPages,
                totalWords,
            })
        );
    }, [organizationId, spaceId, totalPages, totalWords]);

    return (
        <Alert
            size="xsmall"
            kind="info"
            details={
                <p>
                    For <strong className="font-semibold">{totalPages} pages</strong> and content
                    edited daily (
                    <strong>{CONTENT_UPDATE_PER_MONTH} change requests merged per month</strong>),
                    we estimate a total of{' '}
                    <PrettyDollarNumber value={totalPrice} className="font-semibold" /> additional
                    cost per month for your content.
                </p>
            }
        />
    );
}

function estimatePriceOfTranslationRun(wordsCount: number) {
    const price =
        (wordsCount / METER_PRICES[api.BillingMeter.TranslationWords].unitsPerBatch) *
        METER_PRICES[api.BillingMeter.TranslationWords].pricePerBatch;

    return price;
}
